<script setup lang="ts">
/**
 * 資源曲線（CPU／RAM／磁碟三條線）——**純 inline SVG，不引任何圖表庫**（批D）。
 *
 * 兩個實作決定：
 * 1. **用 ResizeObserver 量真實像素寬**、座標直接算 px。用 `viewBox`＋
 *    `preserveAspectRatio="none"` 撐滿會把線寬與文字一起拉歪，得再補
 *    `vector-effect` 與各種校正，不如量寬度乾脆。
 * 2. **null 值斷線不連**：agent 沒回報的區間要看得出是空的，硬把前後兩點連起來
 *    等於偽造資料 → 依 null 切成多段 polyline。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import type { MetricsPoint } from '@/api/hosting'

const props = withDefaults(
  defineProps<{
    points: MetricsPoint[]
    /** 取樣間隔（秒）；只用來在圖上說明密度。 */
    intervalSeconds?: number
    height?: number
  }>(),
  { intervalSeconds: 300, height: 200 },
)

type SeriesKey = 'cpu_pct' | 'mem_used_pct' | 'disk_used_pct_max'
const SERIES: { key: SeriesKey; label: string; cls: string }[] = [
  { key: 'cpu_pct', label: 'CPU', cls: 's-cpu' },
  { key: 'mem_used_pct', label: 'RAM', cls: 's-mem' },
  { key: 'disk_used_pct_max', label: '磁碟', cls: 's-disk' },
]

const PAD = { l: 34, r: 10, t: 10, b: 20 }

// --- 量寬（ResizeObserver）---
const box = ref<HTMLElement | null>(null)
const width = ref(720)
let ro: ResizeObserver | null = null

onMounted(() => {
  const el = box.value
  if (!el) return
  width.value = Math.max(240, el.clientWidth)
  ro = new ResizeObserver((entries) => {
    for (const e of entries) width.value = Math.max(240, Math.round(e.contentRect.width))
  })
  ro.observe(el)
})
onBeforeUnmount(() => {
  ro?.disconnect()
  ro = null
})

const innerW = computed(() => Math.max(1, width.value - PAD.l - PAD.r))
const innerH = computed(() => Math.max(1, props.height - PAD.t - PAD.b))

/** 時間軸範圍（用真實時間，不用索引——這樣資料有缺口時 x 軸不會被壓縮）。 */
const times = computed(() => props.points.map((p) => Date.parse(p.at)).filter((t) => !Number.isNaN(t)))
const t0 = computed(() => (times.value.length ? Math.min(...times.value) : 0))
const t1 = computed(() => (times.value.length ? Math.max(...times.value) : 0))

function xOf(iso: string): number {
  const t = Date.parse(iso)
  if (Number.isNaN(t) || t1.value === t0.value) return PAD.l + innerW.value / 2
  return PAD.l + ((t - t0.value) / (t1.value - t0.value)) * innerW.value
}
function yOf(pct: number): number {
  const v = Math.max(0, Math.min(100, pct))
  return PAD.t + (1 - v / 100) * innerH.value
}

/** 每條線切成「連續有值」的多段；單點段另外用圓點畫（polyline 畫不出單點）。 */
const paths = computed(() =>
  SERIES.map((s) => {
    const segs: { x: number; y: number }[][] = []
    let cur: { x: number; y: number }[] = []
    for (const p of props.points) {
      const v = p[s.key]
      if (v == null) {
        if (cur.length) segs.push(cur)
        cur = []
        continue
      }
      cur.push({ x: xOf(p.at), y: yOf(v) })
    }
    if (cur.length) segs.push(cur)
    return {
      ...s,
      lines: segs.filter((g) => g.length > 1).map((g) => g.map((q) => `${q.x},${q.y}`).join(' ')),
      dots: segs.filter((g) => g.length === 1).map((g) => g[0]),
    }
  }),
)

const yTicks = [0, 50, 100]
/**
 * x 軸最多 5 個刻度（含頭尾）。頭尾的 `anchor` 改成 start／end——用 middle 的話
 * 第一個與最後一個標籤會各有一半被畫到繪圖區外（右邊那個直接被容器切掉）。
 */
const xTicks = computed(() => {
  const pts = props.points
  const label = (i: number, n: number) => (i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle')
  if (pts.length < 2) return pts.map((p) => ({ x: xOf(p.at), label: fmtClock(p.at), anchor: 'middle' }))
  const n = Math.min(5, pts.length)
  const out: { x: number; label: string; anchor: string }[] = []
  for (let i = 0; i < n; i++) {
    const p = pts[Math.round((i * (pts.length - 1)) / (n - 1))]
    out.push({ x: xOf(p.at), label: fmtClock(p.at), anchor: label(i, n) })
  }
  return out
})

function fmtClock(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}`
}
function fmtStamp(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const d = new Date(t)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

// --- hover：找時間軸上最近的一點 ---
const hoverIdx = ref<number | null>(null)
const hover = computed(() => {
  const i = hoverIdx.value
  if (i == null || i < 0 || i >= props.points.length) return null
  const p = props.points[i]
  return {
    x: xOf(p.at),
    stamp: fmtStamp(p.at),
    rows: SERIES.map((s) => ({ label: s.label, cls: s.cls, v: p[s.key] })),
    dots: SERIES.map((s) => ({ cls: s.cls, v: p[s.key], y: p[s.key] == null ? 0 : yOf(p[s.key]!) })).filter(
      (d) => d.v != null,
    ),
  }
})

function onMove(e: MouseEvent) {
  const el = box.value
  if (!el || !props.points.length) return
  const x = e.clientX - el.getBoundingClientRect().left
  let best = 0
  let bestD = Infinity
  for (let i = 0; i < props.points.length; i++) {
    const d = Math.abs(xOf(props.points[i].at) - x)
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  hoverIdx.value = best
}
function onLeave() {
  hoverIdx.value = null
}

/** tooltip 靠左還是靠右（避免超出容器）。 */
const tipStyle = computed<Record<string, string>>(() => {
  const h = hover.value
  if (!h) return {}
  // 靠右邊界時改成 right 定位，否則 tooltip 會被容器切掉。
  const flip = h.x > width.value - 150
  const style: Record<string, string> = {}
  if (flip) style.right = `${Math.max(4, width.value - h.x + 8)}px`
  else style.left = `${h.x + 8}px`
  return style
})

function pctText(v: number | null | undefined): string {
  return v == null ? '無資料' : `${Math.round(v)}%`
}
</script>

<template>
  <div ref="box" class="chart-wrap">
    <div class="legend">
      <span v-for="s in SERIES" :key="s.key" class="lg" :class="s.cls"><i /> {{ s.label }}</span>
      <span class="spacer" />
      <span class="subtle">{{ points.length }} 個取樣點・每 {{ Math.round(intervalSeconds / 60) }} 分鐘一點</span>
    </div>

    <div v-if="!points.length" class="empty">
      <div class="empty-sub">尚無取樣資料（agent 每次心跳寫一筆，新註冊的主機要等幾分鐘）。</div>
    </div>

    <div v-else class="plot" :style="{ height: `${height}px` }" @mousemove="onMove" @mouseleave="onLeave">
      <svg :width="width" :height="height" role="img" aria-label="主機資源曲線">
        <!-- 水平格線＋y 軸標籤 -->
        <g class="grid">
          <template v-for="t in yTicks" :key="t">
            <line :x1="PAD.l" :x2="width - PAD.r" :y1="yOf(t)" :y2="yOf(t)" />
            <text :x="PAD.l - 6" :y="yOf(t) + 3" text-anchor="end">{{ t }}%</text>
          </template>
        </g>
        <!-- x 軸標籤 -->
        <g class="xax">
          <text v-for="(t, i) in xTicks" :key="i" :x="t.x" :y="height - 6" :text-anchor="t.anchor">
            {{ t.label }}
          </text>
        </g>
        <!-- 三條線 -->
        <g class="series">
          <template v-for="s in paths" :key="s.key">
            <polyline v-for="(pts, i) in s.lines" :key="`l${i}`" :class="s.cls" :points="pts" />
            <circle v-for="(d, i) in s.dots" :key="`d${i}`" :class="s.cls" :cx="d.x" :cy="d.y" r="2" />
          </template>
        </g>
        <!-- hover 指示 -->
        <g v-if="hover" class="hov">
          <line :x1="hover.x" :x2="hover.x" :y1="PAD.t" :y2="height - PAD.b" />
          <circle v-for="(d, i) in hover.dots" :key="i" :class="d.cls" :cx="hover.x" :cy="d.y" r="3.5" />
        </g>
      </svg>

      <div v-if="hover" class="tip" :style="tipStyle">
        <div class="tstamp">{{ hover.stamp }}</div>
        <div v-for="r in hover.rows" :key="r.label" class="trow" :class="r.cls">
          <i /><span class="tl">{{ r.label }}</span><span class="tv">{{ pctText(r.v) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart-wrap {
  position: relative;
  width: 100%;
  min-width: 0;
}
.legend {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin-bottom: var(--sp-2);
  font-size: var(--fs-11);
  color: var(--fg-3);
}
.legend .spacer {
  flex: 1;
}
.lg {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.lg i,
.trow i {
  display: inline-block;
  width: 9px;
  height: 3px;
  border-radius: 2px;
  background: currentColor;
}
.s-cpu {
  color: var(--brand-500);
}
.s-mem {
  color: var(--info);
}
.s-disk {
  color: var(--warning);
}
.plot {
  position: relative;
}
svg {
  display: block;
}
.grid line {
  stroke: var(--border-2);
  stroke-width: 1;
}
.grid text,
.xax text {
  fill: var(--fg-4);
  font-size: 10px;
}
.series polyline {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linejoin: round;
  stroke-linecap: round;
}
.series circle,
.hov circle {
  fill: currentColor;
  stroke: var(--bg-panel);
  stroke-width: 1;
}
.hov line {
  stroke: var(--border-strong);
  stroke-width: 1;
  stroke-dasharray: 3 3;
}
.tip {
  position: absolute;
  top: 4px;
  z-index: 5;
  min-width: 120px;
  padding: 6px 9px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  box-shadow: var(--shadow-md);
  pointer-events: none;
  font-size: var(--fs-11);
}
.tstamp {
  color: var(--fg-3);
  margin-bottom: 3px;
  font-variant-numeric: tabular-nums;
}
.trow {
  display: flex;
  align-items: center;
  gap: 5px;
}
.trow .tl {
  flex: 1;
  color: var(--fg-2);
}
.trow .tv {
  color: var(--fg);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
</style>
